import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Alert, Button } from "reactstrap";
import { Theme } from "@rjsf/bootstrap-4";
import { withTheme, utils } from "@rjsf/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import Select from "react-select";

import ErrorScreen from "../../../../components/error/ErrorScreen";
import Loader from "../../../../components/Loader";
import { alertContext } from "../../../../components/modal/ModalComponent";
import useFetch from "../../../../components/useFetch";
import { getCokkie } from "../../../login/Login";
import { CustomWidget } from "../../../../components/widgets/CustomWidgets";
import TaskSummary from "../../../../components/modal/NavPanes/TaskSummary";
import {
  ArrowRightIcon,
  BackIcon,
  CheckCircleIcon,
  CloseIcon,
  DatasetIcon,
  PlusIcon,
  UploadIcon,
  VersionsIcon2,
} from "../../../Common/SvgIcons/WorkflowIcons";
import LabelChipsInput, { isLabelsPromptField } from "../LabelChipsInput";
import PromptAutocompleteInput from "./PromptAutocompleteInput";
import {
  formatDuplicateLabelsError,
  getDuplicateLabelNamesInSchema,
  validateDuplicateLabelsInSchema,
} from "../labelValidationUtils";
import { UnlabeledTextWidget, unlabeledRjsfWidgets, LabelsPromptArrayField, isNamePromptLabelArray } from "../DatasetFormUI";
import "../AddDataset.scss";
import "./GenerateTaskCollection.scss";
import { CheckIcon } from "lucide-react";

const registry = utils.getDefaultRegistry();
const defaultFileWidget = registry.widgets["FileWidget"];
const DefaultArrayField = registry.fields["ArrayField"];

Theme.widgets = {
  ...Theme.widgets,
  FileWidget: defaultFileWidget,
};

const Form = withTheme(Theme);

const dependencyValueMatches = (dependencySchema, value) => {
  if (!dependencySchema || value === undefined || value === null) return false;

  if (dependencySchema.const !== undefined) {
    return String(dependencySchema.const) === String(value);
  }

  return Array.isArray(dependencySchema.enum)
    ? dependencySchema.enum.some((option) => String(option) === String(value))
    : false;
};

const getDependentProperty = (schema, parentField, parentValue, childField) => {
  const branches = schema?.dependencies?.[parentField]?.oneOf || [];
  const branch = branches.find((candidate) =>
    dependencyValueMatches(candidate?.properties?.[parentField], parentValue)
  );

  return branch?.properties?.[childField];
};

const getSelectedVersionImageCount = (formData = {}, schema, props = {}) => {
  if (props.selectedByImages) {
    return Array.isArray(props.images) ? props.images.length : 0;
  }

  const selectedVersion = formData["Dataset Version"];
  if (selectedVersion === undefined || selectedVersion === null || selectedVersion === "") {
    return Number(formData["Total Images"] || 0);
  }

  const countProperty = getDependentProperty(
    schema,
    "Dataset Version",
    selectedVersion,
    "Total Images"
  );
  const count = countProperty?.default;
  return count === undefined || count === null ? 0 : Number(count) || 0;
};

const taskSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 48,
    borderRadius: 10,
    borderColor: state.isFocused
      ? "var(--leading-color)"
      : "color-mix(in srgb, var(--glass-color) 72%, #94a3b8)",
    background: state.isFocused ? "var(--bg-color3)" : "var(--bg-color8)",
    boxShadow: state.isFocused
      ? "inset 0 0 0 1px color-mix(in srgb, var(--leading-color) 16%, transparent), 0 0 0 0.16rem rgba(90, 84, 249, 0.12)"
      : "inset 0 0 0 1px rgba(148, 163, 184, 0.08)",
    cursor: "pointer",
    ":hover": {
      borderColor: "color-mix(in srgb, var(--leading-color) 45%, var(--glass-color))",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0.2rem 0.85rem",
  }),
  singleValue: (base) => ({
    ...base,
    color: "var(--text-color)",
    fontWeight: 500,
  }),
  placeholder: (base) => ({
    ...base,
    color: "#98a2b3",
  }),
  indicatorSeparator: (base) => ({
    ...base,
    display: "none",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: "#8a94a6",
  }),
  menu: (base) => ({
    ...base,
    zIndex: 20,
    overflow: "hidden",
    borderRadius: 10,
    border: "1px solid var(--glass-color)",
    background: "var(--bg-color8)",
    boxShadow: "0 12px 28px rgba(15, 23, 42, 0.14)",
  }),
};

export default function GenerateTaskCollection(props) {
  const setAlert = useContext(alertContext);
  const { projectId } = useParams();

  const [schema, setSchema] = useState();
  const [uischema, setUischema] = useState();
  const [submited, setSubmited] = useState({});
  const [dataset, setdataset] = useState();
  const [datasetversion, setdatasetversion] = useState();
  const [activeStep, setActiveStep] = useState(0);
  const [loadingAction, setLoadingAction] = useState(null);
  const [submitError, setSubmitError] = useState("");
  const [selectedOptionLabels, setSelectedOptionLabels] = useState({});
  const [annotationGuidelineFilename, setAnnotationGuidelineFilename] = useState("");
  const [promptSuggestionsProjectId, setPromptSuggestionsProjectId] = useState("");
  const hasSelectedImages = Array.isArray(props.images) && props.images.length > 0;
  const shouldSendSelectedImagesFlag = Boolean(
    props.selectedByImages && hasSelectedImages
  );
  const selectedImagesQuery = shouldSendSelectedImagesFlag
    ? `&selectby_images=true&selected_image_count=${props.images.length}`
    : "";

  const url = props.version_id
    ? `forms/tasks/create_task_collection?dataset_version_id=${props.version_id}${selectedImagesQuery}`
    : `forms/tasks/create_task_collection?project_id=${projectId}${selectedImagesQuery}`;

  const { data, loading, error, setLoad } = useFetch(url, "get");
  const steps = data?.steppers || [];

  const resetFormState = () => {
    setSubmited({});
    setdataset(undefined);
    setdatasetversion(undefined);
    setActiveStep(0);
    setLoadingAction(null);
    setSubmitError("");
    setSelectedOptionLabels({});
    setAnnotationGuidelineFilename("");
    setPromptSuggestionsProjectId(data?.prompt_suggestions_project_id || projectId || "");
    setUischema(data?.ui_schema);
    setSchema(data?.json_schema);
  };

  const closeDrawer = () => {
    props?.setOpenModal?.("");
    props?.close?.("");
  };

  const closeForm = () => {
    resetFormState();
    closeDrawer();
  };

  useEffect(() => {
    if (!data) return;

    setSubmited({});
    setdataset(undefined);
    setdatasetversion(undefined);
    setActiveStep(0);
    setLoadingAction(null);
    setSubmitError("");
    setSelectedOptionLabels({});
    setAnnotationGuidelineFilename("");
    setPromptSuggestionsProjectId(data?.prompt_suggestions_project_id || projectId || "");
    setUischema(data?.ui_schema);
    setSchema(data?.json_schema);
  }, [data, projectId]);

  const replaceUnderscoresInSchemaTitles = (schemaObj) => {
    if (!schemaObj || typeof schemaObj !== "object") return schemaObj;

    const newSchema = { ...schemaObj };

    if (newSchema.title) {
      newSchema.title = newSchema.title.replace(/_/g, " ");
    }

    if (newSchema.properties) {
      newSchema.properties = Object.keys(newSchema.properties).reduce(
        (acc, key) => {
          acc[key] = replaceUnderscoresInSchemaTitles(newSchema.properties[key]);
          return acc;
        },
        {}
      );
    }

    if (newSchema.dependencies) {
      newSchema.dependencies = Object.keys(newSchema.dependencies).reduce(
        (acc, key) => {
          const dependency = newSchema.dependencies[key];

          if (Array.isArray(dependency?.oneOf)) {
            acc[key] = {
              ...dependency,
              oneOf: dependency.oneOf.map((dep) =>
                replaceUnderscoresInSchemaTitles(dep)
              ),
            };
          } else {
            acc[key] = replaceUnderscoresInSchemaTitles(dependency);
          }

          return acc;
        },
        {}
      );
    }

    return newSchema;
  };

  const ensureTaskNameRequired = (schemaObj) => {
    if (!schemaObj || typeof schemaObj !== "object") return schemaObj;

    const hasTaskNameField = Boolean(schemaObj?.properties?.["Task Name"]);
    if (!hasTaskNameField) return schemaObj;

    const requiredFields = Array.isArray(schemaObj.required) ? schemaObj.required : [];
    if (requiredFields.includes("Task Name")) return schemaObj;

    return {
      ...schemaObj,
      required: [...requiredFields, "Task Name"],
    };
  };

  const updatedSchema = useMemo(
    () => ensureTaskNameRequired(replaceUnderscoresInSchemaTitles(schema)),
    [schema]
  );

  const postStepData = async (payload, step) => {
    const cokkie = getCokkie("jwtToken");

    const response = await fetch(`${window["ENV"].API_URL}/${url}&step=${step}`, {
      method: "post",
      headers: {
        Authorization: `Bearer ${cokkie}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result?.detail || "Request failed");
    }

    return result;
  };

  const AI_ASSIST_DISABLED_KEYS = [
    "AI Assist",
    "Model Details",
    "Experiment Details",
    "Foundational Model Details",
    "Skip model prediction with user annotations",
    "Overlap Threshold",
    "Parallel AI Assist Pipelines",
    "merge_iou",
    "tile_size",
    "tile_overlap",
    "min_area_threshold",
    "max_area_threshold",
    "Model Name",
    "Model Labels",
    "Dataset Label",
    "Labels",
    "labels",
    "Score Threshold",
    "LABELS PROMPT KEY",
    "Label Map",
    "model_name",
    "exp_name",
    "Experiment Name",
  ];

  const AI_ASSIST_FOUNDATION_ONLY_KEYS = [
    "Foundational Model Details",
    "merge_iou",
    "tile_size",
    "tile_overlap",
    "min_area_threshold",
    "max_area_threshold",
    "Model Labels",
    "Dataset Label",
    "Labels",
    "labels",
    "LABELS PROMPT KEY",
    "Label Map",
  ];

  const stripKeysFromPayload = (payload = {}, keys = []) => {
    const nextPayload = { ...payload };

    keys.forEach((key) => {
      delete nextPayload[key];
    });

    return nextPayload;
  };

  const ANNOTATION_GUIDELINE_FIELD = "Upload Annotation Guidelines";
  const ANNOTATION_GUIDELINE_FILENAME_FIELD = "Upload Annotation Guidelines Name";
  const ANNOTATION_GUIDELINE_FILENAME_KEY = "annotation_guideline_filename";

  const isGuidelineDataPayload = (value) =>
    typeof value === "string" && value.trim().startsWith("data:");

  const getGuidelineFilenameFromPayload = (payload = {}) => {
    const filename =
      payload[ANNOTATION_GUIDELINE_FILENAME_FIELD] ||
      payload[ANNOTATION_GUIDELINE_FILENAME_KEY];

    if (filename && !isGuidelineDataPayload(filename)) return filename;

    const uploadValue = payload[ANNOTATION_GUIDELINE_FIELD];
    return uploadValue && !isGuidelineDataPayload(uploadValue) ? uploadValue : "";
  };

  const getGuidelineMimeTypeFromPayload = (payload = {}) => {
    const uploadValue = payload[ANNOTATION_GUIDELINE_FIELD];
    if (!isGuidelineDataPayload(uploadValue)) return "";
    return uploadValue.slice(5, uploadValue.indexOf(";"));
  };

  const withAnnotationGuidelineFilename = (
    payload = {},
    { replaceUploadValue = false } = {}
  ) => {
    const filename =
      annotationGuidelineFilename || getGuidelineFilenameFromPayload(payload);

    if (!filename) return payload;

    const nextPayload = {
      ...payload,
      [ANNOTATION_GUIDELINE_FILENAME_FIELD]: filename,
      [ANNOTATION_GUIDELINE_FILENAME_KEY]: filename,
    };
    const mediaType = getGuidelineMimeTypeFromPayload(payload);
    if (mediaType) nextPayload["Upload Annotation Guidelines Type"] = mediaType;

    if (
      replaceUploadValue &&
      nextPayload[ANNOTATION_GUIDELINE_FIELD] &&
      !isGuidelineDataPayload(nextPayload[ANNOTATION_GUIDELINE_FIELD])
    ) {
      nextPayload[ANNOTATION_GUIDELINE_FIELD] = filename;
    }

    return nextPayload;
  };

  const isAiAssistEnabledValue = (value) =>
    value === true || value === "Yes" || value === "yes" || value === "true";

  const applyServerResult = (result, fallbackPayload = {}) => {
    setUischema(result?.ui_schema);

    if (result?.summary) {
      const sanitizedSummary = sanitizeAiAssistPayload(result.summary, result.summary);
      const summaryWithGuidelineFilename = withAnnotationGuidelineFilename(
        sanitizedSummary,
        { replaceUploadValue: true }
      );
      setSubmited(summaryWithGuidelineFilename);
      setSchema({ configuration: summaryWithGuidelineFilename });
    } else if (fallbackPayload && Object.keys(fallbackPayload).length > 0) {
      setSchema(result?.json_schema);
      const sanitizedFallbackPayload = sanitizeAiAssistPayload(
        fallbackPayload,
        fallbackPayload
      );
      const fallbackWithGuidelineFilename = withAnnotationGuidelineFilename(
        sanitizedFallbackPayload,
        { replaceUploadValue: true }
      );

      setSubmited((prev) => ({
        ...prev,
        ...fallbackWithGuidelineFilename,
      }));
    } else {
      setSchema(result?.json_schema);
    }

    setActiveStep((prev) =>
      result?.active_step !== undefined ? result.active_step : prev + 1
    );
  };

  const sanitizeAiAssistPayload = (payload = {}, currentStepData = payload) => {
    if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
      return payload;
    }

    const normalizedPayload = { ...payload };
    const foundationalDetails = normalizedPayload["Foundational Model Details"];
    const modelSelection = foundationalDetails?.["Model Name"];
    const modelSelectionSchema =
      updatedSchema?.properties?.["Foundational Model Details"]?.properties?.[
        "Model Name"
      ];
    if (modelSelectionSchema && modelSelection !== undefined) {
      const enumValues = modelSelectionSchema.enum || [];
      const enumNames = modelSelectionSchema.enumNames || [];
      const displayIndex = enumNames.findIndex(
        (name) => String(name) === String(modelSelection)
      );
      const oneOfOption = (modelSelectionSchema.oneOf || []).find(
        (option) => String(option?.title) === String(modelSelection)
      );
      const canonicalSelection =
        displayIndex >= 0 ? enumValues[displayIndex] : oneOfOption?.const;
      if (canonicalSelection !== undefined) {
        normalizedPayload["Foundational Model Details"] = {
          ...foundationalDetails,
          "Model Name": canonicalSelection,
        };
      }
    }

    const sourceData =
      currentStepData && typeof currentStepData === "object" && !Array.isArray(currentStepData)
        ? currentStepData
        : normalizedPayload;
    const useAiAssistValue =
      sourceData["Use AI Assist"] !== undefined
        ? sourceData["Use AI Assist"]
        : normalizedPayload["Use AI Assist"];

    if (useAiAssistValue === undefined) {
      return normalizedPayload;
    }

    if (!isAiAssistEnabledValue(useAiAssistValue)) {
      return stripKeysFromPayload(normalizedPayload, AI_ASSIST_DISABLED_KEYS);
    }

    const aiAssistMode =
      sourceData["AI Assist"] !== undefined
        ? sourceData["AI Assist"]
        : normalizedPayload["AI Assist"];

    if (aiAssistMode === "Use Model") {
      return stripKeysFromPayload(normalizedPayload, [
        "Experiment Details",
        "Foundational Model Details",
        "merge_iou",
        "tile_size",
        "tile_overlap",
        "min_area_threshold",
        "max_area_threshold",
        "Model Labels",
        "Dataset Label",
        "Labels",
        "labels",
        "LABELS PROMPT KEY",
        "Label Map",
        "exp_name",
        "Experiment Name",
      ]);
    }

    if (aiAssistMode === "Use Platform Experiment") {
      return stripKeysFromPayload(normalizedPayload, [
        "Model Details",
        ...AI_ASSIST_FOUNDATION_ONLY_KEYS,
        "model_name",
      ]);
    }

    if (aiAssistMode) {
      return stripKeysFromPayload(normalizedPayload, [
        "Model Details",
        "Experiment Details",
        "model_name",
        "exp_name",
        "Experiment Name",
      ]);
    }

    return stripKeysFromPayload(
      normalizedPayload,
      AI_ASSIST_DISABLED_KEYS.filter((key) => key !== "AI Assist")
    );
  };

  const handleFormChange = ({ formData }, changedFieldId) => {
    const sanitizedFormData = sanitizeAiAssistPayload(formData, formData);
    const previousDataset = submited["Dataset"];
    const previousVersion = submited["Dataset Version"];
    const selectedDataset = sanitizedFormData["Dataset"];
    const selectedVersion = sanitizedFormData["Dataset Version"];
    const datasetChanged =
      selectedDataset !== undefined && selectedDataset !== previousDataset;
    const versionChanged =
      selectedVersion !== undefined && selectedVersion !== previousVersion;

    const nextFormData = { ...sanitizedFormData };
    if (datasetChanged) {
      delete nextFormData["Dataset Version"];
      delete nextFormData["Total Images"];
    } else if (selectedVersion !== undefined) {
      nextFormData["Total Images"] = getSelectedVersionImageCount(
        nextFormData,
        updatedSchema,
        props
      );
    }

    if (datasetChanged || versionChanged) {
      setSelectedOptionLabels((previousLabels) => {
        const nextLabels = { ...previousLabels };
        if (datasetChanged) delete nextLabels.Dataset;
        delete nextLabels["Dataset Version"];
        return nextLabels;
      });
    }

    setSubmited((previousFormData) =>
      enforceAnnotationFilterExclusivity(
        nextFormData,
        previousFormData,
        changedFieldId,
        formSchema
      )
    );
  };

  const onSubmit = async ({ formData }, e) => {
    e.preventDefault();
    setLoad(true);
    setLoadingAction("next");
    setSubmitError("");

    try {
      const updatedFormData = {
        images: props.images,
        ...(shouldSendSelectedImagesFlag
          ? { "Total Images": props.images.length }
          : {}),
        ...formData,
      };

      if (props.version_id) {
        if (updatedFormData["Dataset"]) {
          if (!dataset) setdataset(updatedFormData["Dataset"]);
          updatedFormData["Dataset"] = props.datasetId;
        }

        if (updatedFormData["Dataset Version"]) {
          if (!datasetversion) setdatasetversion(updatedFormData["Dataset Version"]);
          updatedFormData["Dataset Version"] = props.version_id;
        }
      }

      const currentStepData = props.version_id ? updatedFormData : formData;
      const sanitizedPayload = sanitizeAiAssistPayload(
        props.version_id
          ? { ...submited, ...updatedFormData }
          : { ...submited, ...formData },
        currentStepData
      );
      const payload = enforceAnnotationFilterExclusivity(
        sanitizedPayload,
        submited,
        "",
        formSchema
      );
      const payloadWithGuidelineFilename = withAnnotationGuidelineFilename(
        payload,
        { replaceUploadValue: true }
      );
      const duplicateLabelsInSchema = getDuplicateLabelNamesInSchema(
        formSchema,
        payloadWithGuidelineFilename
      );

      if (duplicateLabelsInSchema.length > 0) {
        const message = formatDuplicateLabelsError(duplicateLabelsInSchema);
        setSubmited(payloadWithGuidelineFilename);
        setSubmitError(message);
        setAlert?.({ message, status: "error" });
        return;
      }

      setSubmited(payloadWithGuidelineFilename);

      const result = await postStepData(payloadWithGuidelineFilename, activeStep);
      applyServerResult(result, payloadWithGuidelineFilename);
    } catch (e) {
      const message = e.message || "Failed to move to the next step.";
      setSubmitError(message);
      setAlert?.(message);
    } finally {
      setLoad(false);
      setLoadingAction(null);
    }
  };

  const back = async (e) => {
    e.preventDefault();
    setLoad(true);
    setLoadingAction("back");
    setSubmitError("");

    try {
      const previousStep = activeStep - 2;
      const payload = withAnnotationGuidelineFilename(
        sanitizeAiAssistPayload({ ...submited }, submited),
        { replaceUploadValue: true }
      );

      const result = await postStepData(payload, previousStep);

      setUischema(result?.ui_schema);

      if (result?.summary) {
        const sanitizedSummary = sanitizeAiAssistPayload(result.summary, result.summary);
        const summaryWithGuidelineFilename = withAnnotationGuidelineFilename(
          sanitizedSummary,
          { replaceUploadValue: true }
        );
        setSubmited(summaryWithGuidelineFilename);
        setSchema({ configuration: summaryWithGuidelineFilename });
      } else {
        setSchema(result?.json_schema);
      }

      setActiveStep(result?.active_step ?? 0);
    } catch (e) {
      const message = e.message || "Failed to load the previous step.";
      setSubmitError(message);
      setAlert?.(message);
    } finally {
      setLoad(false);
      setLoadingAction(null);
    }
  };

  const final = async (e) => {
    e.preventDefault();
    setLoad(true);
    setLoadingAction("submit");
    setSubmitError("");

    try {
      const payload = withAnnotationGuidelineFilename(
        { ...schema.configuration },
        { replaceUploadValue: true }
      );
      const result = await postStepData(payload, activeStep);

      resetFormState();

      // silently notify parent to refresh metrics + task collection list
      if (props.onSuccess) {
        props.onSuccess(result);
      } else {
        closeDrawer();
      }
    } catch (e) {
      const message = e.message || "Failed to create the task collection.";
      setSubmitError(message);
      setAlert?.(message);
    } finally {
      setLoad(false);
      setLoadingAction(null);
    }
  };

  const isLastStep = Boolean(schema) && activeStep === steps.length - 1;
  const currentStepLabel = steps?.[activeStep] || "Configuration";
  const totalSteps = steps.length || 1;
  const currentStepNumber = Math.min(activeStep + 1, totalSteps);
  const visibleHeaderSteps = steps.slice(0, currentStepNumber);
  const currentFormData = submited || {};
  const datasetName = currentFormData["Dataset"];
  const datasetVersionName = currentFormData["Dataset Version"];
  const getSelectedOptionLabel = (fieldName, value) => {
    if (value === undefined || value === null || value === "") return "";

    const fieldSchema =
      updatedSchema?.properties?.[fieldName] ||
      (fieldName === "Dataset Version"
        ? getDependentProperty(updatedSchema, "Dataset", datasetName, fieldName)
        : null);
    if (!fieldSchema || typeof fieldSchema !== "object") {
      return String(value);
    }

    try {
      const enumOptions = utils.optionsList(fieldSchema);
      const matchedOption = enumOptions.find(
        (option) => String(option.value) === String(value)
      );

      return matchedOption?.label || String(value);
    } catch (error) {
      return String(value);
    }
  };
  const summaryDatasetName =
    selectedOptionLabels["Dataset"] || getSelectedOptionLabel("Dataset", datasetName);
  const summaryDatasetVersionName =
    currentFormData.ds_version ||
    selectedOptionLabels["Dataset Version"] ||
    getSelectedOptionLabel("Dataset Version", datasetVersionName) ||
    currentFormData.ds_version;
  const isFilterByMapEnabled = Boolean(
    currentFormData["Filter By Map"] ?? currentFormData["filter by MAP"]
  );
  const totalImages = getSelectedVersionImageCount(
    currentFormData,
    updatedSchema,
    props
  );
  const batchSize = Number(currentFormData["Batch Size"] || 0);
  const estimatedTasks =
    totalImages > 0 && batchSize > 0 ? Math.ceil(totalImages / batchSize) : 0;
  const isFirstStep = activeStep === 0 && !isLastStep;

  const fieldLabelMap = {
    "Task Collection Name": "Task Collection Name",
    "Task Name": "Task Name",
    "Image Quality": "Image Quality",
    Dataset: "Select Dataset",
    "Dataset Version": "Dataset Version",
    "Total Images": "Total Images to Annotate",
    "Batch Size": "Images Per Task",
    "Images Per Task": "Images Per Task",
    Annotators: "Assign Annotators",
    Validators: "Validator",
    "Annotator Flags": "Assign Annotator Flags",
    "Validator Flags": "Assign Validator Flags",
    "Upload Annotation Guidelines": "Upload Annotation Guidelines",
  };

  const isTaskSummaryReady = Boolean(datasetName || datasetVersionName || totalImages);

  const getFieldClassName = (name) => {
    if (name === "Task Collection Name" || name === "Task Name") {
      return "task-field-full";
    }
    if (
      name === "Dataset" ||
      name === "Dataset Version" ||
      name === "Total Images" ||
      name === "Batch Size" ||
      name === "Images Per Task"
    ) {
      return "task-field-half";
    }

    return "task-field-full";
  };

  const getFieldSlot = (name) => {
    if (name === "Task Collection Name" || name === "Task Name") return "hero";
    if (name === "Dataset" || name === "Dataset Version") return "dataset";
    if (
      name === "Total Images" ||
      name === "Batch Size" ||
      name === "Images Per Task"
    ) {
      return "sizing";
    }
    if (name === "Annotator Flags" || name === "Validator Flags") return "flags";

    return "default";
  };

  const normalizeFieldName = (name = "") =>
    String(name || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "");

  const isPostFiltersStep = (stepLabel = "", properties = []) => {
    const normalizedStep = String(stepLabel || "").toLowerCase();
    const propertyNames = properties.map((property) => normalizeFieldName(property.name));

    return (
      normalizedStep.includes("post filter") ||
      (propertyNames.includes("filterbymap") &&
        propertyNames.some((name) => ["map", "maptitle", "mapp", "mapscore"].includes(name)))
    );
  };

  const isFiltersStepSchema = (schemaNode) => {
    const propertyKeys = Object.keys(schemaNode?.properties || {});
    return (
      activeStep === 2 &&
      propertyKeys.some((key) => ["prefilters", "postfilters"].includes(normalizeFieldName(key)))
    );
  };

  const isFilterGroupSchema = (schemaNode, title = "") => {
    const normalizedTitle = normalizeFieldName(title || schemaNode?.title);
    return ["prefilters", "postfilters"].includes(normalizedTitle);
  };

  const isFilterToggleSchema = (schemaNode) => {
    if (schemaNode?.type !== "object" || !schemaNode?.properties?.apply) return false;
    return true;
  };

  const isAiAssistStepSchema = (schemaNode) => {
    const propertyKeys = Object.keys(schemaNode?.properties || {}).map((key) =>
      normalizeFieldName(key)
    );

    return activeStep === 3 && propertyKeys.includes("useaiassist");
  };

  const isAiAssistDetailsSchema = (schemaNode, title = "") => {
    const normalizedTitle = normalizeFieldName(title || schemaNode?.title);
    return ["experimentdetails", "modeldetails", "foundationalmodeldetails"].includes(
      normalizedTitle
    );
  };

  const isAiAssistModeField = (fieldName = "", fieldSchema = {}) => {
    return (
      activeStep === 3 &&
      normalizeFieldName(fieldName || fieldSchema?.title) === "aiassist"
    );
  };

  const isAiAssistToggleField = (fieldName = "", fieldSchema = {}) => {
    return (
      activeStep === 3 &&
      normalizeFieldName(fieldName || fieldSchema?.title) === "useaiassist"
    );
  };

  const isAiAssistDetailField = (fieldName = "", fieldSchema = {}) => {
    return isAiAssistDetailsSchema(fieldSchema, fieldName);
  };

  const getAssignmentFieldOrder = (name = "") => {
    const normalized = String(name).toLowerCase();

    if (normalized === "annotators") return 0;
    if (normalized === "annotator flags") return 1;
    if (normalized === "validators") return 2;
    if (normalized === "validator flags") return 3;
    if (normalized.includes("guideline")) return 4;

    return 99;
  };

  const getPropertyByName = (properties = [], name = "") =>
    properties.find((property) => normalizeFieldName(property.name) === normalizeFieldName(name));

  const isImageQualityFieldName = (fieldName = "") =>
    normalizeFieldName(fieldName) === "imagequality";

  const buildFlagsFromDescription = (text = "") => {
    const match = String(text).match(/\[(.*)\]/);
    if (!match?.[1]) return [];

    return match[1]
      .split(",")
      .map((item) => item.replace(/['"]/g, "").trim())
      .filter(Boolean);
  };

  const isLabelOrTagField = (fieldName = "") => {
    const normalized = String(fieldName).toLowerCase();
    return (
      normalized.includes("label") ||
      normalized.includes("tag") ||
      normalized.includes("flag")
    );
  };

  const isFlagField = (fieldName = "") =>
    String(fieldName).toLowerCase().includes("flag");

  const hasSelectableChoices = (schemaNode = {}) =>
    Array.isArray(schemaNode?.enum) ||
    Array.isArray(schemaNode?.items?.enum) ||
    Array.isArray(schemaNode?.oneOf) ||
    Array.isArray(schemaNode?.items?.oneOf);

  const isWithinFilterGroup = (fieldPath = []) =>
    fieldPath.some((segment) =>
      ["prefilters", "postfilters"].includes(normalizeFieldName(segment))
    );

  const isFilterLabelOrTagField = (fieldName = "", fieldPath = []) =>
    isWithinFilterGroup(fieldPath) &&
    ["labels", "tags"].includes(normalizeFieldName(fieldName));

  const getAnnotationFilterKind = (value = "") => {
    const normalizedValue = normalizeFieldName(value);

    if (
      normalizedValue.includes("unannotated") ||
      normalizedValue.includes("missingannotations")
    ) {
      return "unannotated";
    }

    if (
      normalizedValue.includes("annotated") ||
      normalizedValue.includes("onlyannotations")
    ) {
      return "annotated";
    }

    return null;
  };

  const isLabelsFilterName = (value = "") => {
    const normalizedValue = normalizeFieldName(value);

    return (
      normalizedValue === "label" ||
      normalizedValue === "labels" ||
      normalizedValue.includes("filterbylabel") ||
      normalizedValue.includes("filterlabel") ||
      normalizedValue.includes("labelfilter")
    );
  };

  const getValueAtPath = (source = {}, path = []) =>
    path.reduce((currentValue, pathKey) => currentValue?.[pathKey], source);

  const setValueAtPath = (source = {}, path = [], value) => {
    if (path.length === 0) {
      return source;
    }

    const [pathKey, ...remainingPath] = path;
    const nextSource = Array.isArray(source) ? [...source] : { ...(source || {}) };

    if (remainingPath.length === 0) {
      nextSource[pathKey] = value;
      return nextSource;
    }

    nextSource[pathKey] = setValueAtPath(
      nextSource[pathKey],
      remainingPath,
      value
    );

    return nextSource;
  };

  const setUiSchemaAtPath = (source = {}, path = [], updater) => {
    if (path.length === 0) {
      return source || {};
    }

    const [pathKey, ...remainingPath] = path;
    const nextSource = { ...(source || {}) };

    if (remainingPath.length === 0) {
      nextSource[pathKey] = updater(nextSource[pathKey] || {});
      return nextSource;
    }

    nextSource[pathKey] = setUiSchemaAtPath(
      nextSource[pathKey],
      remainingPath,
      updater
    );

    return nextSource;
  };

  const getBooleanTogglePath = (schemaNode = {}, path = []) => {
    if (schemaNode?.type === "boolean") {
      return path;
    }

    const booleanProperties = Object.entries(schemaNode?.properties || {}).filter(
      ([, propertySchema]) => propertySchema?.type === "boolean"
    );

    if (booleanProperties.length === 0) {
      return null;
    }

    const [toggleKey] =
      booleanProperties.find(
        ([key, propertySchema]) =>
          normalizeFieldName(key) === "apply" ||
          normalizeFieldName(propertySchema?.title) === "apply"
      ) || booleanProperties[0];

    return [...path, toggleKey];
  };

  const collectAnnotationFilterPaths = (
    schemaNode = {},
    path = [],
    result = {}
  ) => {
    Object.entries(schemaNode?.properties || {}).forEach(([key, propertySchema]) => {
      const currentPath = [...path, key];
      const filterKind = getAnnotationFilterKind(
        `${key} ${propertySchema?.title || ""}`
      );

      if (filterKind) {
        const togglePath = getBooleanTogglePath(propertySchema, currentPath);

        if (togglePath) {
          result[filterKind] = togglePath;
        }
      }

      collectAnnotationFilterPaths(propertySchema, currentPath, result);
    });

    return result;
  };

  const getLabelsFilterPath = (schemaNode = {}, path = []) => {
    const propertyEntries = Object.entries(schemaNode?.properties || {});

    for (const [key, propertySchema] of propertyEntries) {
      const currentPath = [...path, key];
      const isLabelsFilter = isLabelsFilterName(
        `${key} ${propertySchema?.title || ""}`
      );

      if (isLabelsFilter) {
        const togglePath = getBooleanTogglePath(propertySchema, currentPath);

        if (togglePath) {
          return togglePath;
        }
      }

      const nestedLabelsPath = getLabelsFilterPath(propertySchema, currentPath);

      if (nestedLabelsPath) {
        return nestedLabelsPath;
      }
    }

    return null;
  };

  const clearLabelsFilterWhenUnannotated = (
    nextFormData,
    annotationPaths,
    schemaNode
  ) => {
    const unannotatedPath = annotationPaths?.unannotated;
    const labelsPath = getLabelsFilterPath(schemaNode);

    if (!unannotatedPath || !labelsPath) {
      return nextFormData;
    }

    const unannotatedSelected = Boolean(
      getValueAtPath(nextFormData, unannotatedPath)
    );

    if (!unannotatedSelected) {
      return nextFormData;
    }

    return setValueAtPath(nextFormData, labelsPath, false);
  };

  const disableLabelsFilterForUnannotated = (
    nextUiSchema,
    schemaNode,
    formData = {}
  ) => {
    const annotationPaths = collectAnnotationFilterPaths(schemaNode);
    const unannotatedPath = annotationPaths.unannotated;
    const labelsPath = getLabelsFilterPath(schemaNode);

    if (
      !unannotatedPath ||
      !labelsPath ||
      !getValueAtPath(formData, unannotatedPath)
    ) {
      return nextUiSchema;
    }

    return setUiSchemaAtPath(nextUiSchema, labelsPath, (uiNode = {}) => ({
      ...uiNode,
      "ui:disabled": true,
    }));
  };

  const enforceAnnotationFilterExclusivity = (
    nextFormData,
    previousFormData,
    changedFieldId,
    schemaNode
  ) => {
    const annotationPaths = collectAnnotationFilterPaths(schemaNode);
    const annotatedPath = annotationPaths.annotated;
    const unannotatedPath = annotationPaths.unannotated;

    if (!annotatedPath || !unannotatedPath) {
      return nextFormData;
    }

    let resolvedFormData = nextFormData;
    const annotatedSelected = Boolean(getValueAtPath(nextFormData, annotatedPath));
    const unannotatedSelected = Boolean(
      getValueAtPath(nextFormData, unannotatedPath)
    );

    if (annotatedSelected && unannotatedSelected) {
      const changedKind = getAnnotationFilterKind(changedFieldId);

      if (changedKind === "annotated") {
        resolvedFormData = setValueAtPath(nextFormData, unannotatedPath, false);
      } else if (changedKind === "unannotated") {
        resolvedFormData = setValueAtPath(nextFormData, annotatedPath, false);
      } else {
        const previousAnnotatedSelected = Boolean(
          getValueAtPath(previousFormData, annotatedPath)
        );
        const previousUnannotatedSelected = Boolean(
          getValueAtPath(previousFormData, unannotatedPath)
        );

        if (!previousAnnotatedSelected && annotatedSelected) {
          resolvedFormData = setValueAtPath(nextFormData, unannotatedPath, false);
        } else if (!previousUnannotatedSelected && unannotatedSelected) {
          resolvedFormData = setValueAtPath(nextFormData, annotatedPath, false);
        } else {
          resolvedFormData = setValueAtPath(nextFormData, unannotatedPath, false);
        }
      }
    }

    return clearLabelsFilterWhenUnannotated(
      resolvedFormData,
      annotationPaths,
      schemaNode
    );
  };

  const shouldUseTaskFlagsField = (schemaNode, fieldName = "", fieldPath = []) => {
    if (schemaNode?.type !== "array") return false;
    if (isFlagField(fieldName)) return true;
    if (isFilterLabelOrTagField(fieldName, fieldPath)) return true;
    if (!isLabelOrTagField(fieldName)) return false;

    return !hasSelectableChoices(schemaNode);
  };

  const enhanceNestedUiSchema = (
    schemaNode,
    uiNode = {},
    fieldName = "",
    ancestry = []
  ) => {
    if (!schemaNode || typeof schemaNode !== "object") {
      return uiNode || {};
    }

    const nextUiNode = { ...(uiNode || {}) };
    const currentPath = [...ancestry, fieldName].filter(Boolean);

    if (fieldName) {
      nextUiNode["ui:label"] = false;
      nextUiNode["ui:options"] = {
        ...(nextUiNode["ui:options"] || {}),
        label: false,
      };
    }

    if (isImageQualityFieldName(fieldName)) {
      nextUiNode["ui:title"] = false;
      nextUiNode["ui:label"] = false;
      nextUiNode["ui:options"] = {
        ...(nextUiNode["ui:options"] || {}),
        label: false,
      };
    }

    if (schemaNode.type === "boolean") {
      const normalizedFieldName = normalizeFieldName(fieldName);
      nextUiNode["ui:widget"] =
        (activeStep === 2 && normalizedFieldName === "apply") ||
        normalizedFieldName === "useaiassist"
          ? "taskSwitch"
          : "taskCheckboxCard";
    }

    if (schemaNode.type === "string" && normalizeFieldName(fieldName) === "aiassist") {
      nextUiNode["ui:widget"] = "taskSegmentedChoice";
      nextUiNode["ui:options"] = {
        ...(nextUiNode["ui:options"] || {}),
        label: false,
        description: false,
      };
    }

    if (normalizeFieldName(fieldName) === "aiassist") {
      nextUiNode["ui:widget"] = "taskSegmentedChoice";
    }

    if (normalizeFieldName(fieldName) === "totalimages") {
      nextUiNode["ui:widget"] = "taskStaticNumber";
    }

    if (isNamePromptLabelArray(schemaNode)) {
      nextUiNode["ui:field"] = "LabelsPromptArrayField";
      nextUiNode["ui:options"] = {
        ...(nextUiNode["ui:options"] || {}),
        label: false,
        description: false,
      };
    }

    if (isLabelsPromptField(fieldName, schemaNode)) {
      nextUiNode["ui:field"] = "labelChipsField";
      nextUiNode["ui:widget"] = "labelChipsInput";
    }

    if (schemaNode?.["x-ai-prompt-autocomplete"]) {
      nextUiNode["ui:widget"] = "promptAutocomplete";
      nextUiNode["ui:options"] = {
        ...(nextUiNode["ui:options"] || {}),
        projectId: promptSuggestionsProjectId,
        apiUrl: window["ENV"].API_URL,
        authToken: getCokkie("jwtToken"),
      };
    }

    if (shouldUseTaskFlagsField(schemaNode, fieldName, currentPath)) {
      nextUiNode["ui:field"] = "taskFlagsField";
      nextUiNode["ui:options"] = {
        ...(nextUiNode["ui:options"] || {}),
        placeholder: `Add ${fieldName}`,
        layout: isFilterLabelOrTagField(fieldName, currentPath) ? "filter" : "default",
        fieldLabel: getReadableFieldName(fieldName),
        descriptionText:
          nextUiNode?.["ui:help"] ||
          schemaNode?.description ||
          "",
      };
    }

    if (schemaNode.format === "data-url") {
      nextUiNode["ui:widget"] = "taskFileUpload";
    }

    if (schemaNode.properties && typeof schemaNode.properties === "object") {
      Object.keys(schemaNode.properties).forEach((key) => {
        nextUiNode[key] = enhanceNestedUiSchema(
          schemaNode.properties[key],
          nextUiNode[key],
          key,
          currentPath
        );
      });
    }

    if (schemaNode.items && typeof schemaNode.items === "object") {
      nextUiNode.items = enhanceNestedUiSchema(
        schemaNode.items,
        nextUiNode.items,
        fieldName,
        currentPath
      );
    }

    if (schemaNode.dependencies && typeof schemaNode.dependencies === "object") {
      Object.keys(schemaNode.dependencies).forEach((depKey) => {
        const dependency = schemaNode.dependencies[depKey];

        if (Array.isArray(dependency?.oneOf)) {
          nextUiNode[depKey] = {
            ...(nextUiNode[depKey] || {}),
            oneOf: dependency.oneOf.map((branch, index) =>
              enhanceNestedUiSchema(
                branch,
                nextUiNode?.[depKey]?.oneOf?.[index],
                depKey,
                currentPath
              )
            ),
          };
        }
      });
    }

    return nextUiNode;
  };

  const enhanceUiSchemaByKey = (
    schemaNode,
    uiNode = {},
    fieldName = "",
    ancestry = []
  ) => {
    if (!uiNode || typeof uiNode !== "object" || Array.isArray(uiNode)) {
      return uiNode;
    }

    const nextUiNode = { ...uiNode };
    const currentPath = [...ancestry, fieldName].filter(Boolean);

    if (isImageQualityFieldName(fieldName)) {
      nextUiNode["ui:title"] = false;
      nextUiNode["ui:label"] = false;
      nextUiNode["ui:options"] = {
        ...(nextUiNode["ui:options"] || {}),
        label: false,
      };
    }

    if (shouldUseTaskFlagsField(schemaNode, fieldName, currentPath)) {
      nextUiNode["ui:field"] = "taskFlagsField";
      nextUiNode["ui:options"] = {
        ...(nextUiNode["ui:options"] || {}),
        placeholder: `Add ${fieldName}`,
        layout: isFilterLabelOrTagField(fieldName, currentPath) ? "filter" : "default",
        fieldLabel: getReadableFieldName(fieldName),
        descriptionText: nextUiNode?.["ui:help"] || "",
      };
    }

    if (isLabelsPromptField(fieldName, schemaNode)) {
      nextUiNode["ui:field"] = "labelChipsField";
      nextUiNode["ui:widget"] = "labelChipsInput";
    }

    if (normalizeFieldName(fieldName) === "aiassist") {
      nextUiNode["ui:widget"] = "taskSegmentedChoice";
      nextUiNode["ui:options"] = {
        ...(nextUiNode["ui:options"] || {}),
        label: false,
        description: false,
      };
    }

    if (isNamePromptLabelArray(schemaNode)) {
      nextUiNode["ui:field"] = "LabelsPromptArrayField";
      nextUiNode["ui:options"] = {
        ...(nextUiNode["ui:options"] || {}),
        label: false,
        description: false,
      };
    }

    if (schemaNode?.["x-ai-prompt-autocomplete"]) {
      nextUiNode["ui:widget"] = "promptAutocomplete";
      nextUiNode["ui:options"] = {
        ...(nextUiNode["ui:options"] || {}),
        projectId: promptSuggestionsProjectId,
        apiUrl: window["ENV"].API_URL,
        authToken: getCokkie("jwtToken"),
      };
    }

    if (schemaNode?.properties && typeof schemaNode.properties === "object") {
      Object.keys(schemaNode.properties).forEach((key) => {
        nextUiNode[key] = enhanceUiSchemaByKey(
          schemaNode.properties[key],
          nextUiNode[key],
          key,
          currentPath
        );
      });
    }

    if (schemaNode?.items && typeof schemaNode.items === "object") {
      nextUiNode.items = enhanceUiSchemaByKey(
        schemaNode.items,
        nextUiNode.items,
        fieldName,
        currentPath
      );
    }

    if (schemaNode?.dependencies && typeof schemaNode.dependencies === "object") {
      Object.keys(schemaNode.dependencies).forEach((depKey) => {
        const dependency = schemaNode.dependencies[depKey];

        if (Array.isArray(dependency?.oneOf)) {
          nextUiNode[depKey] = {
            ...(nextUiNode[depKey] || {}),
            oneOf: dependency.oneOf.map((branch, index) =>
              enhanceUiSchemaByKey(
                branch,
                nextUiNode?.[depKey]?.oneOf?.[index],
                depKey,
                currentPath
              )
            ),
          };
        }
      });
    }

    return nextUiNode;
  };

  const hideDefaultLabels = (schemaNode, uiNode = {}) => {
    if (!schemaNode || typeof schemaNode !== "object") {
      return uiNode || {};
    }

    const nextUiNode = { ...(uiNode || {}) };

    if (schemaNode.properties && typeof schemaNode.properties === "object") {
      Object.keys(schemaNode.properties).forEach((key) => {
        const childSchema = schemaNode.properties[key];
        const childUiNode = { ...(nextUiNode[key] || {}) };

        childUiNode["ui:label"] = false;
        childUiNode["ui:options"] = {
          ...(childUiNode["ui:options"] || {}),
          label: false,
        };

        nextUiNode[key] = hideDefaultLabels(childSchema, childUiNode);
      });
    }

    if (schemaNode.items && typeof schemaNode.items === "object") {
      nextUiNode.items = hideDefaultLabels(schemaNode.items, nextUiNode.items || {});
    }

    return nextUiNode;
  };

  const getReadableFieldName = (fieldName = "") => {
    return fieldLabelMap[fieldName] || String(fieldName || "").replace(/_/g, " ");
  };

  const isMapMetricFieldName = (fieldName = "") => {
    const normalized = normalizeFieldName(fieldName);
    return ["map", "maptitle", "mapp", "mapscore"].includes(normalized);
  };

  const getStepDescription = (stepLabel, schemaNode) => {
    const fieldNames = Object.keys(schemaNode?.properties || {}).filter(Boolean).filter(
      (name) => name !== "configuration"
    );
    const visibleFieldNames = fieldNames
      .map(getReadableFieldName);

    const hasTaskName = fieldNames.includes("Task Name");
    const hasDataset = fieldNames.includes("Dataset");
    const hasDatasetVersion = fieldNames.includes("Dataset Version");
    const hasTotalImages = fieldNames.includes("Total Images");
    const hasBatchSize =
      fieldNames.includes("Batch Size") || fieldNames.includes("Images Per Task");

    if (hasTaskName && hasDataset && hasDatasetVersion) {
      return "Name this task, choose the dataset, and pick the dataset version to continue.";
    }

    if (hasTaskName && hasDataset) {
      return "Name this task and choose the dataset you want to use.";
    }

    if (hasTotalImages && hasBatchSize) {
      return "Set how many images to include and how they should be split into tasks.";
    }

    if (visibleFieldNames.length > 0) {
      if (visibleFieldNames.length === 1) {
        return `Set the ${visibleFieldNames[0].toLowerCase()} for this step.`;
      }

      if (visibleFieldNames.length === 2) {
        return `Complete the ${visibleFieldNames[0].toLowerCase()} and ${visibleFieldNames[1].toLowerCase()} for this step.`;
      }

      const preview = visibleFieldNames.slice(0, 3).join(", ");
      return `Complete ${preview}${visibleFieldNames.length > 3 ? ", and the remaining fields" : ""} to continue.`;
    }

    const normalized = String(stepLabel || "").toLowerCase();

    if (normalized.includes("data")) {
      return "Choose the dataset version and give this labeling task a clear, recognizable name.";
    }

    if (normalized.includes("annotation")) {
      return "Set task sizing, assign annotators and validators, add workflow flags, and attach annotation guidelines.";
    }

    if (normalized.includes("assign")) {
      return "Configure who will work on this task and how responsibilities are distributed.";
    }

    if (normalized.includes("review") || normalized.includes("summary")) {
      return "Review the configuration carefully before creating the labeling task.";
    }

    return "Fill in the required information for this step before moving forward.";
  };

  const renderTaskSummaryCard = ({
    summaryDatasetName,
    summaryDatasetVersionName,
    totalImages: summaryTotalImages,
  }) => (
    <div className="task-summary-card container-card p-3">
      <div className="task-summary-card__icon">
        <DatasetIcon className="wf-icon" />
      </div>
      <div className="task-summary-card__content">
        <div className="card-title">Data Source Summary</div>
        <div className="small-text mt-1">
          Dataset: {summaryDatasetName || "Not selected"}{" "}
          {summaryDatasetVersionName ? `• Version: ${summaryDatasetVersionName}` : ""}
          {" • "}
          Available: {summaryTotalImages.toLocaleString()} images
        </div>
      </div>
    </div>
  );

  const objectTemplateStateRef = useRef({});
  objectTemplateStateRef.current = {
    currentFormData,
    currentStepLabel,
    datasetName,
    datasetVersionName,
    summaryDatasetName,
    summaryDatasetVersionName,
    estimatedTasks,
    isFilterByMapEnabled,
    isTaskSummaryReady,
    totalImages,
  };

  const TaskFieldTemplate = useMemo(
    () => (fieldProps) => {
      const {
        id,
        classNames,
        label,
        name,
        required,
        description,
        errors,
        help,
        children,
        hidden,
        schema: fieldSchema,
        uiSchema: fieldUiSchema = {},
      } = fieldProps;

      if (hidden) {
        return <>{children}</>;
      }

      const fallbackLabel =
        fieldLabelMap[name] ||
        fieldLabelMap[fieldSchema?.title] ||
        fieldSchema?.title ||
        label ||
        name;
      const resolvedLabel = fallbackLabel;
      const isStructuredField =
        fieldSchema?.type === "object" || fieldSchema?.type === "array";
      const isImageQualityField = [
        name,
        label,
        fieldSchema?.title,
        resolvedLabel,
      ].some(isImageQualityFieldName);
      const isUploadGuidelinesField =
        name === "Upload Annotation Guidelines" ||
        fieldSchema?.title === "Upload Annotation Guidelines";
      const isFilterApplyField = activeStep === 2 && name === "apply";
      const isAiAssistMode = isAiAssistModeField(name, fieldSchema);
      const isAiAssistToggle = isAiAssistToggleField(name, fieldSchema);
      const isAiAssistSkipPrediction =
        activeStep === 3 &&
        normalizeFieldName(name || fieldSchema?.title) ===
          "skipmodelpredictionwithuserannotations";
      const isAiAssistDetailsField = isAiAssistDetailField(name, fieldSchema);
      const isLabelsPromptArrayField =
        isNamePromptLabelArray(fieldSchema) ||
        fieldUiSchema?.["ui:field"] === "LabelsPromptArrayField";
      const isFilterStructuredField =
        activeStep === 2 &&
        isStructuredField &&
        (
          isFiltersStepSchema(fieldSchema) ||
          isFilterGroupSchema(fieldSchema, name) ||
          isFilterToggleSchema(fieldSchema)
        );
      const shouldHideTemplateMeta =
        isImageQualityField ||
        isFilterApplyField ||
        isFilterStructuredField ||
        isAiAssistMode ||
        isAiAssistToggle ||
        isAiAssistSkipPrediction ||
        isAiAssistDetailsField ||
        isLabelsPromptArrayField;

      return (
        <div
          className={`${classNames} task-collection-field ${
            isStructuredField ? "task-collection-field--group" : ""
          } ${isImageQualityField ? "task-collection-field--flat" : ""} ${
            isUploadGuidelinesField ? "task-collection-field--upload" : ""
          } ${!resolvedLabel ? "task-collection-field--no-label" : ""}`}
        >
          {resolvedLabel && !shouldHideTemplateMeta && (
            <label htmlFor={id} className="form-label">
              {resolvedLabel}
              {required && <span className="text-danger ms-1">*</span>}
            </label>
          )}
          {description && !isUploadGuidelinesField && !shouldHideTemplateMeta && (
            <div className="form-help">{description}</div>
          )}
          <div className="task-collection-field__control">{children}</div>
          {errors}
          {!isUploadGuidelinesField &&
            !shouldHideTemplateMeta &&
            !isLabelsPromptField(name, fieldSchema) &&
            help}
        </div>
      );
    },
    [activeStep]
  );

  const TaskSelectWidget = ({
    schema: fieldSchema,
    value,
    options,
    placeholder,
    disabled,
    onChange,
    label,
  }) => {
    const enumOptions = options?.enumOptions || [];
    const isMulti = fieldSchema?.type === "array";
    const fieldTitle = label || fieldSchema?.title;
    const selected = isMulti
      ? enumOptions.filter((option) => Array.isArray(value) && value.includes(option.value))
      : enumOptions.find((option) => option.value === value) || null;

    useEffect(() => {
      if (!fieldTitle || isMulti) return;

      const selectedLabel = selected?.label;
      if (!selectedLabel) return;

      setSelectedOptionLabels((prev) =>
        prev[fieldTitle] === selectedLabel ? prev : { ...prev, [fieldTitle]: selectedLabel }
      );
    }, [fieldTitle, isMulti, selected]);

    return (
      <Select
        classNamePrefix="task-select"
        isMulti={isMulti}
        value={selected}
        options={enumOptions}
        isDisabled={disabled}
        isClearable={false}
        styles={taskSelectStyles}
        placeholder={placeholder || `Select ${fieldSchema?.title || "option"}...`}
        onChange={(option) => {
          if (isMulti) {
            onChange(Array.isArray(option) ? option.map((item) => item.value) : []);
            return;
          }

          onChange(option?.value);
        }}
      />
    );
  };

  const TaskVersionSelectWidget = ({
    value,
    options = {},
    schema: fieldSchema,
    onChange,
    disabled,
    readonly,
    label,
  }) => {
    const enumOptions =
      options.enumOptions ||
      (fieldSchema?.enum || []).map((item) => ({ value: item, label: item }));

    const hasOptions = enumOptions.length > 0;
    const latestIndex = hasOptions ? enumOptions.length - 1 : -1;
    const versionOptions = hasOptions
      ? enumOptions.map((option, index) => ({
          ...option,
          label:
            index === latestIndex && enumOptions.length > 1
              ? `${option.label} (Latest)`
              : option.label,
        }))
      : [];
    const selected =
      hasOptions &&
      (versionOptions.find((option) => option.value === value) ||
        versionOptions[latestIndex]);
    const fieldTitle = label || fieldSchema?.title;

    useEffect(() => {
      if (!fieldTitle || !selected?.label) return;

      const cleanLabel = selected.label.replace(/\s+\(Latest\)$/, "");
      setSelectedOptionLabels((prev) =>
        prev[fieldTitle] === cleanLabel ? prev : { ...prev, [fieldTitle]: cleanLabel }
      );
    }, [fieldTitle, selected]);

    useEffect(() => {
      if (value !== undefined && value !== null && value !== "") return;
      if (!selected?.value || disabled || readonly) return;

      onChange(selected.value);
    }, [disabled, onChange, readonly, selected, value]);

    if (!hasOptions) {
      return null;
    }

    return (
      <Select
        classNamePrefix="task-select"
        value={selected}
        options={versionOptions}
        isClearable={false}
        isSearchable={false}
        isDisabled={disabled || readonly}
        styles={taskSelectStyles}
        onChange={(option) => onChange(option?.value)}
        menuPlacement="auto"
      />
    );
  };

  const TaskSegmentedChoiceWidget = ({
    value,
    onChange,
    disabled,
    readonly,
    options,
  }) => {
    const enumOptions = options?.enumOptions || [];
    const canEdit = !disabled && !readonly;

    return (
      <div className="task-segmented-choice" role="tablist" aria-label="AI Assist">
        {enumOptions.map((option) => {
          const isSelected = option.value === value;

          return (
            <button
              key={option.value}
              type="button"
              className={`task-segmented-choice__option ${
                isSelected ? "is-selected" : ""
              }`}
              onClick={() => canEdit && onChange(option.value)}
              disabled={!canEdit}
            >
              <span className="task-segmented-choice__icon">
                <CheckIcon className="wf-icon" />
              </span>
              <span>{option.label}</span>
            </button>
          );
        })}
      </div>
    );
  };

  const TaskFileWidget = ({
    onChange,
    disabled,
    readonly,
    label,
  }) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef(null);
    const canEdit = !disabled && !readonly;

    const addFileMetadata = (dataUrl, file) => {
      const separator = dataUrl.indexOf(",");
      if (separator < 0) return dataUrl;
      const header = dataUrl.slice(0, separator).replace(
        ";base64",
        `;name=${encodeURIComponent(file.name)};base64`
      );
      return `${header}${dataUrl.slice(separator)}`;
    };

    const pushFile = (file) => {
      if (!file) return;

      setAnnotationGuidelineFilename(file.name);
      setSelectedFile(file);

      const reader = new FileReader();
      reader.onload = () => {
        onChange(addFileMetadata(reader.result, file));
      };
      reader.readAsDataURL(file);
    };

    const handleFileSelect = (event) => {
      const file = event.target.files?.[0];
      pushFile(file);
    };

    const clearFile = (event) => {
      event.preventDefault();
      event.stopPropagation();
      setSelectedFile(null);
      setAnnotationGuidelineFilename("");
      onChange(undefined);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    };

    return (
      <div className="task-upload-widget">
        {selectedFile && (
          <div className="task-upload-widget__meta">
            <span className="task-upload-widget__filename">{selectedFile.name}</span>
            {canEdit && (
              <button
                type="button"
                className="plain-button2 text-danger fw-bold"
                onClick={clearFile}
              >
                Clear
              </button>
            )}
          </div>
        )}

        <div
          role="button"
          tabIndex={0}
          className={`task-upload-widget__dropzone ${dragOver ? "is-dragover" : ""}`}
          onClick={() => canEdit && fileInputRef.current?.click()}
          onKeyDown={(event) => {
            if ((event.key === "Enter" || event.key === " ") && canEdit) {
              event.preventDefault();
              fileInputRef.current?.click();
            }
          }}
          onDragOver={(event) => {
            event.preventDefault();
            if (canEdit) setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragOver(false);
            if (!canEdit) return;
            pushFile(event.dataTransfer.files?.[0]);
          }}
        >
          <UploadIcon className="em-drop-icon mb-2 wf-icon" />
          <div className="small-bold-text">
            {selectedFile ? "Replace uploaded file" : "Drag & drop your guidelines file"}
          </div>
          <div className="small-text">
            {label || "Upload Annotation Guidelines"} in PDF, DOC, DOCX, TXT, or image format
          </div>
          <button
            type="button"
            className="task-upload-widget__browse"
            disabled={!canEdit}
            onClick={(event) => {
              event.stopPropagation();
              fileInputRef.current?.click();
            }}
          >
            Browse File
          </button>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          style={{ display: "none" }}
          disabled={!canEdit}
          onChange={handleFileSelect}
        />
      </div>
    );
  };

  const TaskCheckboxWidget = ({
    value,
    onChange,
    disabled,
    readonly,
    schema: fieldSchema,
  }) => {
    const checked = Boolean(value);
    const canEdit = !disabled && !readonly;
    const isSkipPrediction =
      normalizeFieldName(fieldSchema?.title) ===
      "skipmodelpredictionwithuserannotations";

    return (
      <button
        type="button"
        className={`task-checkbox-widget ${checked ? "is-checked" : ""} ${
          isSkipPrediction ? "task-checkbox-widget--skip-prediction" : ""
        }`}
        disabled={!canEdit}
        onClick={() => canEdit && onChange(!checked)}
      >
        <span
          className={`task-checkbox-widget__icon ${checked ? "text-primary" : "text-secondary"}`}
        >
          <CheckIcon className="wf-icon" />
        </span>
        <span className="task-checkbox-widget__label">
          {fieldSchema?.title || "Enabled"}
        </span>
      </button>
    );
  };

  const TaskSwitchWidget = ({
    value,
    onChange,
    disabled,
    readonly,
    schema: fieldSchema,
  }) => {
    const checked = Boolean(value);
    const canEdit = !disabled && !readonly;
    const switchId = `task-switch-${String(fieldSchema?.title || "field")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")}`;

    return (
      <label
        htmlFor={switchId}
        className={`task-switch-widget ${checked ? "is-checked" : ""} ${
          !canEdit ? "is-disabled" : ""
        }`}
      >
        <span className="task-switch-widget__label">
          {fieldSchema?.title || "Enabled"}
        </span>
        <span className="task-switch-widget__control">
          <input
            id={switchId}
            type="checkbox"
            checked={checked}
            disabled={!canEdit}
            onChange={(event) => onChange(event.target.checked)}
          />
          <span className="task-switch-widget__slider" />
        </span>
      </label>
    );
  };

  const TaskLabelChipsWidget = ({
    value,
    onChange,
    disabled,
    readonly,
    schema: fieldSchema,
  }) => (
    <LabelChipsInput
      value={value || ""}
      onChange={onChange}
      disabled={disabled}
      readonly={readonly}
      ariaLabel={fieldSchema?.title || "Labels"}
    />
  );

  const TaskLabelChipsField = ({
    formData,
    onChange,
    disabled,
    readonly,
    schema: fieldSchema,
  }) => (
    <LabelChipsInput
      value={formData || ""}
      onChange={onChange}
      disabled={disabled}
      readonly={readonly}
      ariaLabel={fieldSchema?.title || "Labels"}
    />
  );

  const TaskTextWidget = (widgetProps) => {
    const {
      id,
      label,
      name,
      schema: fieldSchema,
      value,
      onChange,
      disabled,
      readonly,
    } = widgetProps;

    const fallbackName = name || label || id?.split("_").pop();

    if (isLabelsPromptField(fallbackName, fieldSchema)) {
      return (
        <LabelChipsInput
          value={value || ""}
          onChange={onChange}
          disabled={disabled}
          readonly={readonly}
          ariaLabel={fieldSchema?.title || label || "Labels"}
        />
      );
    }

    return <UnlabeledTextWidget {...widgetProps} />;
  };

  const TaskStaticNumberWidget = ({ value }) => {
    const numericValue = Number(value);
    const displayValue = Number.isFinite(numericValue)
      ? numericValue.toLocaleString()
      : String(value || 0);

    return <div className="task-static-number">{displayValue}</div>;
  };

  const TaskFlagsWidget = ({
    value = [],
    onChange,
    disabled,
    readonly,
    schema: fieldSchema,
    options = {},
  }) => {
    const [inputValue, setInputValue] = useState("");
    const items = Array.isArray(value) ? value : [];
    const defaultFlags = buildFlagsFromDescription(options.descriptionText);
    const visibleFlags = items.length > 0 ? items : defaultFlags;
    const canEdit = !disabled && !readonly;
    const isFilterLayout = options.layout === "filter";
    const fieldLabel = options.fieldLabel || fieldSchema?.title || "Value";
    const shouldCommitOnDelimiter =
      isFilterLayout && normalizeFieldName(fieldLabel) === "labels";
    const normalizedFieldLabel =
      fieldLabel.charAt(0).toUpperCase() + fieldLabel.slice(1);
    const resolvedPlaceholder = isFilterLayout
      ? `Add ${normalizedFieldLabel}`
      : options.placeholder || "Type a flag";
    const tokenizeInput = (text = "") =>
      String(text)
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    const getInputItems = (text = inputValue) =>
      shouldCommitOnDelimiter
        ? tokenizeInput(text)
        : [String(text).trim()].filter(Boolean);

    useEffect(() => {
      if (items.length === 0 && defaultFlags.length > 0) {
        onChange(defaultFlags);
      }
    }, [defaultFlags, items.length, onChange]);

    const addFlags = (nextItems = getInputItems()) => {
      const cleanedItems = nextItems
        .map((item) => String(item).trim())
        .filter(Boolean);

      if (!cleanedItems.length) return;

      const nextFlags = [...visibleFlags];
      cleanedItems.forEach((item) => {
        if (!nextFlags.includes(item)) {
          nextFlags.push(item);
        }
      });

      if (nextFlags.length !== visibleFlags.length) {
        onChange(nextFlags);
      }

      setInputValue("");
    };

    const handleInputChange = (event) => {
      const nextValue = event.target.value;

      if (!shouldCommitOnDelimiter || !nextValue.includes(",")) {
        setInputValue(nextValue);
        return;
      }

      const tokens = tokenizeInput(nextValue);
      const endsWithDelimiter = /,\s*$/.test(nextValue);
      const hasMultiplePastedTokens = !endsWithDelimiter && tokens.length > 1;
      const completeTokens =
        endsWithDelimiter || hasMultiplePastedTokens ? tokens : tokens.slice(0, -1);
      const remainder =
        endsWithDelimiter || hasMultiplePastedTokens ? "" : tokens[tokens.length - 1] || "";

      if (completeTokens.length) {
        addFlags(completeTokens);
      }

      setInputValue(remainder);
    };

    const handleInputKeyDown = (event) => {
      const shouldCommit =
        event.key === "Enter" ||
        (
          shouldCommitOnDelimiter &&
          event.key === ","
        );

      if (shouldCommit) {
        event.preventDefault();
        addFlags();
      }
    };

    const addFlag = () => {
      if (!inputValue.trim()) return;
      addFlags();
    };

    const removeFlag = (flag) => {
      const next = visibleFlags.filter((item) => item !== flag);
      onChange(next);
    };

    const handleInputBlur = () => {
      if (inputValue.trim()) {
        addFlags();
      }
    };

    return (
      <div className="task-flags-widget">
        <div className="task-flags-widget__input-row">
          <input
            type="text"
            className="task-flags-widget__input"
            value={inputValue}
            placeholder={resolvedPlaceholder}
            disabled={!canEdit}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            onBlur={handleInputBlur}
          />
          <button
            type="button"
            className="task-flags-widget__add-btn"
            onClick={addFlag}
            disabled={!canEdit}
            aria-label={`Add ${normalizedFieldLabel}`}
          >
            <PlusIcon className="wf-icon text-white" />
          </button>
        </div>

        {visibleFlags.length > 0 && (
          <div className="task-flags-widget__chips">
            {visibleFlags.map((flag) => (
              <span key={flag} className="task-flags-widget__chip">
                {flag}
                {canEdit && (
                  <button
                    type="button"
                    className="task-flags-widget__chip-remove"
                    onClick={() => removeFlag(flag)}
                  >
                    ×
                  </button>
                )}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  const TaskFlagsField = ({
    formData,
    onChange,
    disabled,
    readonly,
    schema: fieldSchema,
    uiSchema: fieldUiSchema = {},
  }) => (
    <TaskFlagsWidget
      value={Array.isArray(formData) ? formData : []}
      onChange={onChange}
      disabled={disabled}
      readonly={readonly}
      schema={fieldSchema}
      options={fieldUiSchema?.["ui:options"] || {}}
    />
  );

  const TaskArrayField = (fieldProps) => {
    const {
      schema: fieldSchema,
      uiSchema: fieldUiSchema = {},
      idSchema,
      formData,
      onChange,
      disabled,
      readonly,
    } = fieldProps;

    const arrayId = normalizeFieldName(idSchema?.$id || "");
    const normalizedTitle = normalizeFieldName(fieldSchema?.title || "");
    const options = fieldUiSchema?.["ui:options"] || {};
    const looksLikeFilterLabelOrTagField =
      (
        (arrayId.includes("prefilters") || arrayId.includes("postfilters")) &&
        (arrayId.endsWith("labels") || arrayId.endsWith("tags"))
      ) ||
      (options.layout === "filter" &&
        ["labels", "tags"].includes(normalizedTitle));

    if (looksLikeFilterLabelOrTagField) {
      const fieldName = arrayId.endsWith("labels") ? "Labels" : "Tags";

      return (
        <TaskFlagsWidget
          value={Array.isArray(formData) ? formData : []}
          onChange={onChange}
          disabled={disabled}
          readonly={readonly}
          schema={fieldSchema}
          options={{
            ...options,
            layout: "filter",
            fieldLabel: options.fieldLabel || fieldName,
            placeholder: options.placeholder || `Add ${fieldName}`,
          }}
        />
      );
    }

    if (
      isNamePromptLabelArray(fieldSchema) ||
      fieldUiSchema?.["ui:field"] === "LabelsPromptArrayField"
    ) {
      return <LabelsPromptArrayField {...fieldProps} />;
    }

    return DefaultArrayField ? <DefaultArrayField {...fieldProps} /> : null;
  };

  const enhancedUiSchema = useMemo(() => {
    const schemaEnhanced = enhanceNestedUiSchema(updatedSchema, uischema || {});
    const keyEnhanced = enhanceUiSchemaByKey(updatedSchema, schemaEnhanced);
    const labelHiddenUiSchema = hideDefaultLabels(updatedSchema, keyEnhanced);

    return disableLabelsFilterForUnannotated(
      labelHiddenUiSchema,
      updatedSchema,
      submited
    );
  }, [promptSuggestionsProjectId, submited, uischema, updatedSchema]);

  const formSchema = useMemo(
    () => (updatedSchema ? { ...updatedSchema, title: "" } : updatedSchema),
    [updatedSchema]
  );

  const customValidate = (currentFormData, errors) => {
    validateDuplicateLabelsInSchema(formSchema, currentFormData, errors);
    return errors;
  };

  const getFilterCardTitle = (schemaNode, title = "") => {
    const rawTitle = title || schemaNode?.title || "";
    const normalizedTitle = normalizeFieldName(rawTitle);

    if (normalizedTitle.includes("filterbylabel")) return "Labels";
    if (
      normalizedTitle.includes("filterbyattribute") ||
      normalizedTitle.includes("filterbytag")
    ) {
      return "Tags";
    }

    return (
      schemaNode?.properties?.apply?.title ||
      rawTitle ||
      "Filter"
    );
  };

  const TaskObjectFieldTemplate = useMemo(
    () =>
      ({
        title,
        description,
        properties,
        required,
        uiSchema: objectUiSchema,
        schema: objectSchema,
      }) => {
        const templateState = objectTemplateStateRef.current;
        const liveFormData = templateState.currentFormData || {};

        if (objectUiSchema?.["ui:title"] === false) {
          return <>{properties.map((property) => property.content)}</>;
        }

        if (isFiltersStepSchema(objectSchema)) {
          const preFiltersField = getPropertyByName(properties, "PreFilters");
          const postFiltersField = getPropertyByName(properties, "PostFilters");

          return (
            <section className="task-collection-section">
              <div className="task-filter-layout">
                {preFiltersField && (
                  <div className="task-filter-layout__section">
                    <div className="card-title">Pre Filters</div>
                    <div className="task-filter-layout__content">{preFiltersField.content}</div>
                  </div>
                )}

                {postFiltersField && (
                  <div className="task-filter-layout__section">
                    <div className="card-title">Post Filters</div>
                    <div className="task-filter-layout__content">{postFiltersField.content}</div>
                  </div>
                )}
              </div>
            </section>
          );
        }

        if (isFilterGroupSchema(objectSchema, title)) {
          return (
            <section className="task-filter-group">
              <div className="task-filter-group__grid">
                {properties.map((property) => (
                  <div key={property.name} className="task-filter-group__item">
                    {property.content}
                  </div>
                ))}
              </div>
            </section>
          );
        }

        if (isFilterToggleSchema(objectSchema)) {
          const applyField = getPropertyByName(properties, "apply");
          const dependentFields = properties.filter((property) => property.name !== "apply");
          const filterLabel = getFilterCardTitle(objectSchema, title);

          return (
            <section className="task-filter-card container-card p-3">
              <div className="task-filter-card__header">
                <div className="text">{filterLabel}</div>
                {applyField && (
                  <div className="task-filter-card__toggle">{applyField.content}</div>
                )}
              </div>

              {dependentFields.length > 0 && (
                <div className="task-filter-card__body">
                  {dependentFields.map((property) => (
                    <div key={property.name} className="task-filter-card__field">
                      {property.content}
                    </div>
                  ))}
                </div>
              )}
            </section>
          );
        }

        if (isAiAssistStepSchema(objectSchema)) {
          const useAiAssistField = getPropertyByName(properties, "Use AI Assist");
          const taskNameField = getPropertyByName(properties, "Task Name");
          const aiAssistField = getPropertyByName(properties, "AI Assist");
          const detailField =
            getPropertyByName(properties, "Experiment Details") ||
            getPropertyByName(properties, "Model Details") ||
            getPropertyByName(properties, "Foundational Model Details");
          const skipPredictionField = getPropertyByName(
            properties,
            "Skip model prediction with user annotations"
          );
          const overlapThresholdField = getPropertyByName(properties, "Overlap Threshold");
          const parallelPipelinesField = getPropertyByName(
            properties,
            "Parallel AI Assist Pipelines"
          );
          const foundationalSettings = properties.filter((property) =>
            [
              "merge_iou",
              "tile_size",
              "tile_overlap",
              "min_area_threshold",
              "max_area_threshold",
            ].includes(property.name)
          );
          const handledNames = new Set([
            "Use AI Assist",
            "Task Name",
            "AI Assist",
            "Experiment Details",
            "Model Details",
            "Foundational Model Details",
            "Skip model prediction with user annotations",
            "Overlap Threshold",
            "Parallel AI Assist Pipelines",
            "merge_iou",
            "tile_size",
            "tile_overlap",
            "min_area_threshold",
            "max_area_threshold",
          ]);
          const remainingProperties = properties.filter(
            (property) => !handledNames.has(property.name)
          );
          const isAiAssistEnabled = Boolean(liveFormData["Use AI Assist"]);
          const aiAssistDescription =
            objectUiSchema?.["AI Assist"]?.["ui:description"] ||
            "Use saved/uploaded model or platform executed experiment for annotation";

          return (
            <section className="task-ai-assist container-card">
              <div className="task-ai-assist__toggle-card">
                <div className="task-ai-assist__toggle-copy">
                  <div className="small-bold-text">Use AI to Annotate</div>
                  <div className="small-text">
                    {aiAssistDescription}
                  </div>
                </div>
                {useAiAssistField && (
                  <div className="task-ai-assist__toggle-control">
                    {useAiAssistField.content}
                  </div>
                )}
              </div>

              {isAiAssistEnabled && (
                <div className="task-ai-assist__content">
                  {taskNameField && (
                    <div className="task-ai-assist__task-name">{taskNameField.content}</div>
                  )}

                  {aiAssistField && (
                    <section className="task-ai-assist__mode-card container-card">
                      <label className="form-label mb-2">AI Assist</label>
                      <div className="task-ai-assist__mode-control">
                        {aiAssistField.content}
                      </div>
                    </section>
                  )}

                  {detailField && (
                    <div className="task-ai-assist__primary-details">{detailField.content}</div>
                  )}

                  {(skipPredictionField || overlapThresholdField || parallelPipelinesField) && (
                    <div className="task-ai-assist__grid task-ai-assist__grid--supporting">
                      {skipPredictionField && (
                        <div className="task-ai-assist__skip-prediction">
                          {skipPredictionField.content}
                        </div>
                      )}
                      {overlapThresholdField && <div>{overlapThresholdField.content}</div>}
                      {parallelPipelinesField && <div>{parallelPipelinesField.content}</div>}
                    </div>
                  )}

                  {foundationalSettings.length > 0 && (
                    <section className="task-ai-assist__details-card">
                      <label className="form-label mb-2">
                        Foundational Model Settings
                      </label>
                      <div className="task-ai-assist__grid">
                        {foundationalSettings.map((property) => (
                          <div key={property.name}>{property.content}</div>
                        ))}
                      </div>
                    </section>
                  )}

                  {remainingProperties.map((property) => (
                    <div
                      key={property.name}
                      className={
                        ["labels", "modellabels"].includes(
                          normalizeFieldName(property.name)
                        )
                          ? "task-ai-assist__labels mt-3"
                          : undefined
                      }
                    >
                      {property.content}
                    </div>
                  ))}
                </div>
              )}
            </section>
          );
        }

        if (isAiAssistDetailsSchema(objectSchema, title)) {
          const detailProperties = properties.filter(
            (property) =>
              !["labels", "modellabels"].includes(normalizeFieldName(property.name))
          );
          const labelsProperties = properties.filter((property) =>
            ["labels", "modellabels"].includes(normalizeFieldName(property.name))
          );

          return (
            <>
              <section className="task-ai-assist__details-card">
                <label className="form-label mb-2">
                  {title || objectSchema?.title || "Details"}
                </label>
                <div className="task-ai-assist__grid">
                  {detailProperties.map((property) => (
                    <div key={property.name}>{property.content}</div>
                  ))}
                </div>
              </section>
              {labelsProperties.map((property) => (
                <div key={property.name} className="task-ai-assist__labels mt-3">
                  {property.content}
                </div>
              ))}
            </>
          );
        }

        if (isFirstStep) {
          const grouped = properties.reduce(
            (acc, property) => {
              const slot = getFieldSlot(property.name);
              acc[slot].push(property);
              return acc;
            },
            { hero: [], dataset: [], sizing: [], flags: [], default: [] }
          );

          grouped.hero.sort((a, b) => {
            const order = {
              "Task Name": 0,
              "Task Collection Name": 0,
            };

            return (order[a.name] ?? 99) - (order[b.name] ?? 99);
          });

          return (
            <section className="task-collection-section task-collection-section--mock">
              {grouped.hero.length > 0 && (
                <div className="task-grid task-grid--hero">
                  {grouped.hero.map((property) => (
                    <div
                      key={property.name}
                      className={`task-grid__item ${getFieldClassName(property.name)}`}
                    >
                      {property.content}
                    </div>
                  ))}
                </div>
              )}

              {grouped.dataset.length > 0 && (
                <div className="task-collection-block">
                  <div className="task-grid task-grid--two-col">
                    {grouped.dataset.map((property) => (
                      <div
                        key={property.name}
                        className={`task-grid__item ${getFieldClassName(property.name)}`}
                      >
                        {property.content}
                      </div>
                    ))}
                  </div>

                  {templateState.isTaskSummaryReady &&
                    renderTaskSummaryCard(templateState)}
                </div>
              )}

              {grouped.sizing.length > 0 && (
                <div className="task-collection-block task-collection-block--section">
                  <label className="form-label mb-3">Task Sizing</label>
                  <div className="task-grid task-grid--one-col">
                    {grouped.sizing.map((property) => (
                      <div
                        key={property.name}
                        className={`task-grid__item ${getFieldClassName(property.name)}`}
                      >
                        {property.content}
                      </div>
                    ))}
                  </div>
                  <div className="task-sizing-meta gap-1 d-flex flex-column mt-2 small-text">
                    <div className="task-sizing-meta__item">
                      Max: {templateState.totalImages.toLocaleString()}
                    </div>
                    <div className="task-sizing-meta__item">
                      Est. {templateState.estimatedTasks.toLocaleString()} tasks created
                    </div>
                  </div>
                </div>
              )}

              {grouped.default.length > 0 && (
                <div className="task-collection-block task-collection-block--section">
                  {grouped.default.map((property) => (
                    <div key={property.name} className="task-grid__item task-field-full mt-2">
                      {property.content}
                    </div>
                  ))}
                </div>
              )}

              {grouped.flags.length > 0 && (
                <div className="task-collection-block task-collection-block--section">
                  {grouped.flags.map((property) => (
                    <div key={property.name} className="task-grid__item task-field-full">
                      {property.content}
                    </div>
                  ))}
                </div>
              )}
            </section>
          );
        }

        const grouped = properties.reduce(
          (acc, property) => {
            const slot = getFieldSlot(property.name);
            acc[slot].push(property);
            return acc;
          },
          { hero: [], dataset: [], sizing: [], flags: [], default: [] }
        );
        const hasSizingPair = grouped.sizing.length >= 2;
        const orderedProperties = [...properties].sort((a, b) => {
          const orderDiff =
            getAssignmentFieldOrder(a.name) - getAssignmentFieldOrder(b.name);

          if (orderDiff !== 0) return orderDiff;
          return a.name.localeCompare(b.name);
        });
        const annotatorsField = getPropertyByName(properties, "Annotators");
        const annotatorFlagsField = getPropertyByName(properties, "Annotator Flags");
        const validatorsField = getPropertyByName(properties, "Validators");
        const validatorFlagsField = getPropertyByName(properties, "Validator Flags");
        const filterByMapField = getPropertyByName(properties, "Filter By Map");
        const mapMetricField =
          properties.find((property) => isMapMetricFieldName(property.name)) || null;
        const imageQualityField =
          properties.find((property) => isImageQualityFieldName(property.name)) || null;
        const imageQualitySchema =
          Object.entries(updatedSchema?.properties || {}).find(
            ([key, propertySchema]) =>
              isImageQualityFieldName(key) ||
              isImageQualityFieldName(propertySchema?.title)
          )?.[1] || null;
        const imageQualityChildSchema = imageQualitySchema?.properties
          ? Object.values(imageQualitySchema.properties)[0]
          : null;
        const imageQualityDescription =
          imageQualityChildSchema?.description ||
          (imageQualitySchema?.description &&
          imageQualitySchema.description !== imageQualitySchema?.title
            ? imageQualitySchema.description
            : "") ||
          "Image quality to be rendered. NOTE: Low resolution images can be enhanced by 2x or 4x";
        const guidelinesField =
          properties.find((property) =>
            String(property.name).toLowerCase().includes("guideline")
          ) || null;
        const hasAssignmentGroups = Boolean(
          annotatorsField ||
            annotatorFlagsField ||
            validatorsField ||
            validatorFlagsField
        );
        const renderAssignmentGroups = () => {
          if (!hasAssignmentGroups) return null;

          return (
            <div className="task-assignment-layout">
              {(annotatorsField || annotatorFlagsField) && (
                <div className="task-assignment-card container-card p-3">
                  <div className="card-title d-none">Annotator</div>
                  <div className="task-assignment-card__body">
                    {annotatorsField && <div>{annotatorsField.content}</div>}
                    {annotatorFlagsField && <div>{annotatorFlagsField.content}</div>}
                  </div>
                </div>
              )}

              {(validatorsField || validatorFlagsField) && (
                <div className="task-assignment-card container-card p-3">
                  <div className="card-title d-none">Validator</div>
                  <div className="task-assignment-card__body">
                    {validatorsField && <div>{validatorsField.content}</div>}
                    {validatorFlagsField && <div>{validatorFlagsField.content}</div>}
                  </div>
                </div>
              )}
            </div>
          );
        };
        const orderedNonSizing = orderedProperties.filter(
          (property) =>
            getFieldSlot(property.name) !== "sizing" &&
            property !== imageQualityField &&
            property !== filterByMapField &&
            property !== mapMetricField &&
            !["Annotators", "Annotator Flags", "Validators", "Validator Flags"].includes(
              property.name
            ) &&
            property !== guidelinesField
        );
        const showPostFiltersLayout =
          activeStep === 2 ||
          isPostFiltersStep(templateState.currentStepLabel, properties);
        const renderImageQualityCard = () => {
          if (!imageQualityField) return null;

          return (
            <div className="task-section-card">
              <div className="form-label">Image Quality</div>
              {imageQualityDescription && (
                <div className="task-section-card__description">
                  {imageQualityDescription}
                </div>
              )}
              <div className="task-section-card__content task-section-card__content--flat">
                {imageQualityField.content}
              </div>
            </div>
          );
        };
        const renderSizingCard = () => {
          if (grouped.sizing.length === 0) return null;

          return (
            <div className="task-section-card task-section-card--sizing">
              <div className="task-grid task-grid--two-col task-grid--annotation-sizing">
                {grouped.sizing.map((property) => (
                  <div
                    key={property.name}
                    className={`task-grid__item ${getFieldClassName(property.name)}`}
                  >
                    {property.content}
                  </div>
                ))}
              </div>
            </div>
          );
        };

        return (
          <section className="task-collection-section">
            <div className="task-collection-section__body">
              {showPostFiltersLayout && (
                <div className="task-post-filters">
                  <div className="task-post-filters__heading">Post Filters</div>
                  <div className="task-post-filters__panel">
                    {filterByMapField && (
                      <div className="task-post-filters__row task-post-filters__row--toggle">
                        <div className="task-post-filters__row-label">Filter by Map</div>
                        <div className="task-post-filters__row-control">
                          {filterByMapField.content}
                        </div>
                      </div>
                    )}

                    {mapMetricField && templateState.isFilterByMapEnabled && (
                      <div className="task-post-filters__row task-post-filters__row--stack">
                        <div className="task-post-filters__row-label">Map</div>
                        <div className="task-post-filters__control task-post-filters__control--input">
                          {mapMetricField.content}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {hasSizingPair ? (
                <>
                  {renderImageQualityCard()}

                  {renderSizingCard()}

                  {renderAssignmentGroups()}

                  {guidelinesField && <div>{guidelinesField.content}</div>}

                  {orderedNonSizing.map((property) => (
                    <div key={property.name}>{property.content}</div>
                  ))}
                </>
              ) : (
                <>
                  {renderImageQualityCard()}
                  {renderAssignmentGroups()}
                  {guidelinesField && <div>{guidelinesField.content}</div>}
                  {orderedNonSizing.map((property) => (
                    <div key={property.name}>{property.content}</div>
                  ))}
                </>
              )}
            </div>
          </section>
        );
      },
    [
      activeStep,
      isFirstStep,
      updatedSchema,
    ]
  );

  const customFields = useMemo(
    () => ({
      ArrayField: TaskArrayField,
      taskFlagsField: TaskFlagsField,
      labelChipsField: TaskLabelChipsField,
      LabelsPromptArrayField,
    }),
    []
  );

  const customWidgets = useMemo(
    () => ({
      ...CustomWidget,
      ...unlabeledRjsfWidgets,
      SelectWidget: TaskSelectWidget,
      versionSelect: TaskVersionSelectWidget,
      TextWidget: TaskTextWidget,
      taskFlagsInput: TaskFlagsWidget,
      taskFileUpload: TaskFileWidget,
      taskCheckboxCard: TaskCheckboxWidget,
      taskSwitch: TaskSwitchWidget,
      taskSegmentedChoice: TaskSegmentedChoiceWidget,
      taskStaticNumber: TaskStaticNumberWidget,
      labelChipsInput: TaskLabelChipsWidget,
      promptAutocomplete: PromptAutocompleteInput,
    }),
    []
  );

  return (
    <main className="open-form evaluate-model-ui h-100 d-flex flex-column task-collection-shell">
      <header className="d-flex align-items-start justify-content-between p-3 task-collection-shell__header">
        <div className="d-flex gap-2 align-items-start w-100">
          <VersionsIcon2 className="wf-icon-blue bg-primary text-white" />
          <div className="em-header flex-fill">
            <div className="d-flex justify-content-between w-100">
              <h2 className="main-header mb-0">Create Task Collection</h2>
              <button className="plain-button2 p-0"
                onClick={closeForm}
              >
                <CloseIcon className="wf-icon text-secondary" />
              </button>
            </div>
            <p className="sub-title mb-0">
              Step {currentStepNumber} of {totalSteps}: {currentStepLabel}
            </p>
          </div>
        </div>
      </header>

      <section
        className="dataset-wizard__card flex-fill w-100 task-collection-shell__body"
      >
        {loading ? (
          <div className="task-collection-shell__loader">
            <Loader />
          </div>
        ) : error ? (
          <ErrorScreen message={error} />
        ) : schema ? (
          <>
            <div className="form-shell__intro">
              <div className="task-collection-shell__eyebrow">
                <div className="task-collection-shell__step-flow">
                  {visibleHeaderSteps.map((stepLabel, index) => {
                    const stepNumber = index + 1;
                    const isCurrentHeaderStep = stepNumber === currentStepNumber;

                    return (
                      <div
                        key={`${stepLabel}-${stepNumber}`}
                        className="task-collection-shell__step-item"
                      >
                        {isCurrentHeaderStep ? (
                          <span className="task-collection-shell__step-badge">
                            Step {stepNumber}
                          </span>
                        ) : (
                          <span className="task-collection-shell__step-marker">
                            {stepNumber}
                          </span>
                        )}
                        <span
                          className={`task-collection-shell__step-name ${
                            isCurrentHeaderStep
                              ? "task-collection-shell__step-name--active"
                              : ""
                          }`}
                          style={{color:'#b1c7e8'}}
                        >
                          {stepLabel}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <p className="mb-0 from-shell-sub-text">{getStepDescription(currentStepLabel, updatedSchema)}</p>
            </div>

            {submitError && (
              <Alert color="danger" className="mb-3 py-2">
                {submitError}
              </Alert>
            )}

            {!isLastStep ? (
              <div className="task-collection-stage">
                <Form
                  id="create-task-collection-form"
                  className="task-collection-form"
                  schema={formSchema}
                  uiSchema={enhancedUiSchema}
                  fields={customFields}
                  widgets={customWidgets}
                  formData={submited}
                  onChange={handleFormChange}
                  onSubmit={onSubmit}
                  customValidate={customValidate}
                  showErrorList={false}
                  FieldTemplate={TaskFieldTemplate}
                  ObjectFieldTemplate={TaskObjectFieldTemplate}
                >
                  <Button className="d-none" />
                </Form>
              </div>
            ) : (
              <div className="px-2 task-collection-summary">
                <TaskSummary
                  expData={schema}
                  dataset={dataset}
                  datasetversion={datasetversion}
                  version_id={props.version_id}
                />
              </div>
            )}
          </>
        ) : (
          <ErrorScreen
            message={data?.detail ?? "No form Found"}
            sub={data?.detail}
          />
        )}
      </section>

      {schema && (
        <div className="dataset-footer">
          <div className="dataset-footer__actions">
            {activeStep === 0 ? (
              <div />
            ) : (
              <Button
                type="button"
                className="bg-secondary mt-0 text-white py-2"
                onClick={back}
                disabled={loading}
              >
                {loadingAction === "back" ? (
                  <FontAwesomeIcon className="me-2 text-white" icon={faSpinner} spin />
                ) : (
                  <BackIcon className="wf-icon me-2 text-white" />
                )}
                Back
              </Button>
            )}

            {!isLastStep ? (
              <Button
                type="submit"
                form="create-task-collection-form"
                color="primary py-2 m-0 fw-bold"
                disabled={loading}
              >
                {isFirstStep ? "Next Step" : "Next Step"}&nbsp;
                {loadingAction === "next" ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                  <ArrowRightIcon className="wf-icon text-white" />
                )}
              </Button>
            ) : (
              <Button
                type="button"
                onClick={final}
                color="primary py-2 m-0 fw-bold"
                disabled={loading}
              >
                Submit&nbsp;
                {loadingAction === "submit" ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                  // <PlusIcon className="wf-icon text-white pb-1" />
                  <></>
                )}
              </Button>
            )}
          </div>
        </div>
      )}
    </main>
  );
}
